import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import analytics from "@/lib/analytics";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import logger from "@/lib/logger";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [polls, setPolls] = useState(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n >= MAX_POLLS) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          analytics.track("subscription_activated", {
            session_id: sessionId,
            promo_code: data.promo_code,
            trial_days: data.trial_days,
          });
          refresh();
          return;
        }
        if (data.status === "expired" || data.status === "failed") {
          setStatus(data.status);
          return;
        }
      } catch (err) {
        // Transient network / 5xx — log and keep polling until MAX_POLLS.
        logger.warn("PaymentSuccess poll failed:", err?.response?.status || err?.message);
      }
      setPolls(n + 1);
      setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="vq-card p-10 max-w-md w-full text-center">
        {status === "paid" ? (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-6" />
            <div className="vq-overline mb-2">Payment confirmed</div>
            <h1 className="font-display font-extrabold text-3xl text-slate-900 mb-4">
              Welcome to Pro.
            </h1>
            <p className="text-slate-600 mb-8">
              Your subscription is active. All Pro features are now unlocked.
            </p>
            <Link to="/dashboard" data-testid="post-payment-dashboard-btn">
              <Button className="w-full h-11 bg-slate-900 hover:bg-[#0055FF] text-white font-semibold">
                Go to Dashboard
              </Button>
            </Link>
          </>
        ) : status === "timeout" || status === "expired" || status === "failed" ? (
          <>
            <div className="vq-overline mb-2 text-red-600">Payment issue</div>
            <h1 className="font-display font-extrabold text-2xl text-slate-900 mb-4">
              {status === "timeout" ? "Confirmation timed out" : `Payment ${status}`}
            </h1>
            <p className="text-slate-600 mb-8">
              We couldn&apos;t confirm the payment. If you were charged, contact support.
            </p>
            <Link to="/pricing">
              <Button variant="outline" className="w-full h-11 border-slate-300 font-semibold">Back to pricing</Button>
            </Link>
          </>
        ) : (
          <>
            <Loader2 className="w-14 h-14 text-slate-900 mx-auto mb-6 animate-spin" />
            <div className="vq-overline mb-2">Verifying</div>
            <h1 className="font-display font-extrabold text-2xl text-slate-900 mb-2">
              Confirming your payment…
            </h1>
            <p className="text-slate-500 text-sm">Attempt {polls + 1} of {MAX_POLLS}</p>
          </>
        )}
      </div>
    </div>
  );
}
